import { type Cff, cffKey } from 'utils/cff'

import type { Stream } from './useAllStreams'

export { type Cff, cffKey, sameCff } from 'utils/cff'

/**
 * A field the operation can open, and what is behind it. `streams` is empty for
 * a field planning knows that no machine has driven yet, and `planningFieldId`
 * is absent for a field with data that was never registered for planning.
 * Neither absence disqualifies a field: each datastore holds fields the other
 * does not, and someone looking for their field is not asking which one it
 * came from.
 */
export type BrowsableField = Cff & {
  planningFieldId: string | undefined
  streams: Stream[]
}

/**
 * The operation's fields, joined across the two datastores that name them.
 * Pure over both lists rather than a hook over both queries, so each client
 * keeps its own query states and the join itself stays one answer.
 *
 * Carries no ordering — which field to show first is the caller's question.
 */
export const browsableFields = ({
  streams,
  planningFields
}: {
  streams: readonly Stream[]
  planningFields: readonly (Cff & { id: string })[]
}): BrowsableField[] => {
  const byField = new Map<string, BrowsableField>()

  for (const stream of streams) {
    const key = cffKey(stream)
    const existing = byField.get(key)
    if (existing === undefined) {
      byField.set(key, {
        client: stream.client,
        farm: stream.farm,
        field: stream.field,
        planningFieldId: undefined,
        streams: [stream]
      })
    } else {
      existing.streams.push(stream)
    }
  }

  for (const planningField of planningFields) {
    const key = cffKey(planningField)
    const existing = byField.get(key)
    if (existing === undefined) {
      byField.set(key, {
        client: planningField.client,
        farm: planningField.farm,
        field: planningField.field,
        planningFieldId: planningField.id,
        streams: []
      })
    } else {
      existing.planningFieldId = planningField.id
    }
  }

  return [...byField.values()]
}
